import { MarketplaceOrderTrackResponse } from 'api/cart/types/cart-mp-tracking.type';
import { OrderTrackResponse } from 'api/order/types';
import { PayloadAction } from '@reduxjs/toolkit/src/createAction';
import { ResponseError } from 'types/error.types';
import { Status } from 'redux/types/state.interface';
import { createSlice } from '@reduxjs/toolkit';
import { fetchMpTrackingItems } from 'components/Cart/feature/actions';
import {
  trackEnterpriseOrder,
  trackOrder,
} from 'components/CustomerService/OrderTracking/feature/actions';

type State = {
  status: Status;
  data: OrderTrackResponse;
  enterpriseOrder: OrderTrackResponse;
  isEnterpriseOrder: boolean;
  originalOrderNumber: string;
  error: ResponseError | null;
  isEmailMismatch: boolean;
  marketplaceOrderData?: MarketplaceOrderTrackResponse | null;
};
const initialState: State = {
  data: {
    order: {},
    parentProducts: [],
    childProducts: [],
    status: [],
    tracking: {},
    driverTracking: undefined,
  },
  enterpriseOrder: {
    order: {},
    parentProducts: [],
    childProducts: [],
    status: [],
    tracking: {},
    driverTracking: undefined,
  },
  isEnterpriseOrder: false,
  originalOrderNumber: '',
  status: Status.INIT,
  error: null,
  isEmailMismatch: false,
  marketplaceOrderData: null,
};

const slice = createSlice({
  name: 'customer-service/order-tracking',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(trackOrder.pending, (state) => {
        state.status = Status.PENDING;
        state.isEmailMismatch = false;
      })
      .addCase(
        trackOrder.fulfilled,
        (state, { payload }: PayloadAction<OrderTrackResponse | undefined>) => {
          if (payload) {
            state.data = payload;
            state.status = Status.FULFILLED;
          } else {
            state.status = Status.NOT_FOUND;
          }
        },
      )
      .addCase(trackOrder.rejected, (state, { payload }) => {
        state.status = Status.ERROR;
        state.isEmailMismatch =
          (payload as ResponseError | undefined)?.status === 401;
      })
      .addCase(trackEnterpriseOrder.pending, (state) => {
        state.status = Status.PENDING;
        state.isEnterpriseOrder = false;
      })
      .addCase(
        trackEnterpriseOrder.fulfilled,
        (state, { payload }: PayloadAction<OrderTrackResponse | undefined>) => {
          if (payload) {
            state.enterpriseOrder = payload;
            state.status = Status.FULFILLED;
            state.isEnterpriseOrder = true;
          } else {
            state.status = Status.NOT_FOUND;
          }
        },
      )
      .addCase(trackEnterpriseOrder.rejected, (state, { payload }) => {
        state.status = Status.ERROR;
        state.isEnterpriseOrder = false;
        state.isEmailMismatch =
          (payload as ResponseError | undefined)?.status === 401;
      })
      .addCase(fetchMpTrackingItems.fulfilled, (state, { payload }) => {
        if (payload) {
          state.marketplaceOrderData = payload;
          state.status = Status.FULFILLED;
        }
      })
      .addCase(fetchMpTrackingItems.pending, (state) => {
        state.status = Status.PENDING;
      })
      .addCase(fetchMpTrackingItems.rejected, (state) => {
        state.status = Status.ERROR;
      })
      .addDefaultCase((state) => state);
  },
  reducers: {
    setOriginalOrderNumber: (state, { payload }: PayloadAction<string>) => {
      state.originalOrderNumber = payload || '';
    },
    reset: () => initialState,
  },
});

export const orderTrackingReducer = slice.reducer;
export const { reset, setOriginalOrderNumber } = slice.actions;
